import dayjs from 'dayjs';
import { PricingSummary } from '../forms/form.types';
import { EventRegistrationForm, EventRegistrationRegistrant } from '../generated/api/dsv-public/model';

export const generateRandomKey: () => string = () => {
  return Math.random().toString(36).slice(-5);
};

export const isEmpty = (obj: Record<string, any>): boolean => {
  return Object.keys(obj).length === 0;
};

export const prettyPrintDateRange = (startDate: string, endDate: string | undefined) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (endDate === undefined || start.isSame(end, 'day')) {
    return start.format('DD.MM.YY');
  } else if (start.isSame(end, 'year')) {
    return `${start.format('DD.MM')} - ${end.format('DD.MM.YY')}`;
  } else {
    return `${start.format('DD.MM.YY')} - ${end.format('DD.MM.YY')}`;
  }
};

export const prettyPrintTimeRange = (startTime: string, endTime: string | undefined) => {
  startTime = startTime.substring(0, 5);
  endTime = endTime?.substring(0, 5);

  if (endTime === undefined) {
    return startTime;
  }

  return `${startTime} - ${endTime}`;
};

export const prettyPrintDate = (date: string | undefined) => {
  if (date === undefined) {
    return '';
  }
  return dayjs(date).format('DD.MM.YY');
};

export const extractFormValuesFromRegistrant = (
  registrant: EventRegistrationRegistrant | undefined,
  registrationForm: EventRegistrationForm | undefined
) => {
  const fieldKeys = registrationForm?.fields?.map(field => field.key) || [];
  return Object.fromEntries(Object.entries(registrant ?? {}).filter(([key]) => fieldKeys.includes(key)));
};

export const addToPricingSummary = (
  pricingSummary: Map<string, PricingSummary>,
  price: number | undefined,
  currency: string | undefined
) => {
  // Skip if currency or price is undefined (or 0)
  if (!currency || !price) {
    return;
  }

  if (pricingSummary.has(currency)) {
    const currencySummary: PricingSummary = pricingSummary.get(currency)!;
    currencySummary.totalPrice += price;
  } else {
    pricingSummary.set(currency, { currency, totalPrice: price });
  }
};

export const reduceToPriceSummaryMap = (listOfPricingSummaries: PricingSummary[][]): Map<string, PricingSummary> => {
  const mergedSummaries: Map<string, PricingSummary> = new Map();
  listOfPricingSummaries.forEach((pricingSummaries: PricingSummary[]) => {
    pricingSummaries.forEach((summary: PricingSummary) => {
      const { currency, totalPrice } = summary;
      addToPricingSummary(mergedSummaries, totalPrice, currency);
    });
  });

  return mergedSummaries;
};

export const getPricingSummary = (
  pricingOptions: { [key: string]: number[] },
  optionsPriceMap: Map<number, number>,
  optionsCurrencyMap: Map<number, string>
): Map<string, PricingSummary> => {
  const optionsPricingSummary: Map<string, PricingSummary> = new Map();
  const options = Object.values(pricingOptions).reduce((prev, cur) => prev.concat(cur), []);
  options.forEach((option: number) => {
    const price = optionsPriceMap.get(option);
    const currency = optionsCurrencyMap.get(option);
    addToPricingSummary(optionsPricingSummary, price, currency);
  });
  return optionsPricingSummary;
};

export const checkIsValidFieldValue = (
  required: boolean,
  valuePattern: string | undefined,
  value: string | undefined
) => {
  const actualValue = value || '';

  if (required && actualValue.length === 0) {
    return false;
  }

  if (valuePattern) {
    const reg = new RegExp(valuePattern);
    if (!reg.test(actualValue)) {
      return false;
    }
  }

  return true;
};
